<template>
  <div class="flex flex-col max-w-[1100px] w-full h-600 mx-auto sm:mb-9" :class="isMobile ? 'u-center' : ''">
    <OBreadcrumb class="capitalize text-md leading-18 mt-6 mb-4 w-[80vw]" :items="breadcrumbRoutes" show-home-page-title />
    <div v-if="!isMobile" class="u-center bg-white border border-[#E6E6E6] rounded-xl">
      <div v-if="!isPlay" class="my-20">
        <OImage loading="lazy" :src="game.coverImage" class="w-[640px] h-[360px] rounded-md mb-8" />
        <OButton color-scheme="green" class="mt-3 w-[640px] h-10 u-center" @click="playGame()">
          <span class="font-semibold text-base">{{ $t('game.play-now') }}</span>
        </OButton>
      </div>
      <div
        v-else-if="game.source === 'mediazone'"
        class="fixed inset-0 w-full h-full xpt-12 bg-white !z-full-page-loading u-center overflow-hidden"
      >
        <!-- ads left -->
        <div class="hidden xl:flex h-full w-48 items-start justify-center">
          <div class="w-40 h-[600px] border border-gray-400 text-gray-400 u-center flex-col mt-3">
            <AdController class="w-full flex justify-center" unit="oyunlarSkyscrapperLeft" v-bind="$attrs" />
          </div>
        </div>
        <!-- game -->
        <div class="flex-1 relative h-full w-full">
          <OButton
            v-if="false"
            pure
            class="u-center -top-10 left-0 pr-4 pl-1 absolute text-white z-2 rounded-full"
            style="background: rgba(51, 51, 51, 0.75)"
            @click="closeGame"
          >
            <OIcon icon="left-white" class="-m-[3px] w-[30px] h-[30px] u-center" svg-style="fill: #fff" />
            <p class="text-md font-normal">{{ $t('profile.back') }}</p>
          </OButton>
          <iframe :src="src" width="100%" height="100%" scrolling="none" frameborder="0" allowfullscreen></iframe>
        </div>
        <!-- ads right -->
        <div class="hidden xl:flex h-full w-48 items-start justify-center">
          <div class="w-40 h-[600px] border border-gray-400 text-gray-400 u-center flex-col mt-3">
            <AdController class="w-full flex justify-center" unit="oyunlarSkyscrapperRight" v-bind="$attrs" />
          </div>
        </div>
      </div>
      <iframe v-else :src="src" width="1100" height="600" scrolling="none" frameborder="0" allowfullscreen></iframe>
    </div>
    <div v-else>
      <div v-if="!isPlay">
        <OImage loading="lazy" :src="game.thumbnail" class="w-[320px] h-[320px] rounded-md" />
        <OButton color-scheme="green" class="mt-3 w-[320px] h-10 u-center my-8" @click="playGame()">
          <span class="font-semibold text-base">{{ $t('game.play') }}</span>
        </OButton>
      </div>
      <div v-else>
        <GameModal :game="game" :src="src" @closeModal="closeGame()" />
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent, onMounted, ref, useRoute, useContext } from '@nuxtjs/composition-api';
import AdController from '@/modules/ads/components/AdController';
import gameData from '../gameData';
import GameModal from './GameModal.vue';
import { useDevice } from '~/composables/useDevice';
import { HOST_URL, GAMES_PATH } from '~/constants/config';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { useAuth } from '~/modules/auth/state/useAuth';
import { generateUniqueId } from '~/composables/utils';
import { OButton, OImage, OBreadcrumb, OIcon } from '~/modules/common/components/ui';

export default defineComponent({
  components: { OButton, OImage, OBreadcrumb, GameModal, AdController, OIcon },
  setup() {
    const route = useRoute();
    const { isLoggedIn, user } = useAuth();
    const { isMobile } = useDevice();
    const { i18n } = useContext();
    const isPlay = ref(false);
    const { fullPath } = route.value;
    const slug = route.value.params.slug;
    const game = gameData.filter((g) => {
      return g.slug === slug;
    })[0];

    const URLS_WITHOUT_QUERY = ['yandex', 'mediazone'];
    let src = URLS_WITHOUT_QUERY.includes(game.source) ? game.url : game.url + HOST_URL + fullPath;

    // set user_token to mediazone game url
    if (game.source === 'mediazone' && process.client) {
      let anonymPlayerId = localStorage.getItem('o-player-id');
      if (!anonymPlayerId) {
        anonymPlayerId = generateUniqueId();
        localStorage.setItem('o-player-id', anonymPlayerId);
      }
      const id = isLoggedIn.value ? user.value?._id : anonymPlayerId;
      localStorage.setItem('o-player-id', id);
      src = src.replace('user_token', 'onedio_' + id);
    }

    const breadcrumbRoutes = [
      { href: `/${GAMES_PATH}`, name: `${i18n.t('game.games')}` },
      { href: route.value.fullPath, name: game.name },
    ];
    const setGameStatus = () => {
      localStore.setItem(game.slug, true);
    };

    const playGame = () => {
      setGameStatus();
      isPlay.value = true;
      window.dataLayer.push({
        event: 'game_interactions',
        event_action: 'game_play',
        game_name: game.name,
      });
      if (game.source === 'mediazone') {
        fixZIndex('remove');
        lockScroll();
      }
    };

    const closeGame = () => {
      isPlay.value = false;
      if (game.source === 'mediazone') {
        fixZIndex('add');
        unlockScroll();
      }
    };

    // masthead projects spoil the z-index of the page container
    const fixZIndex = (action) => {
      const pageContainer = document.getElementsByClassName('layout__main')[0];
      if (pageContainer) {
        if (action === 'add') {
          pageContainer.classList.add('flex-1', 'flex', 'flex-col');
        } else {
          pageContainer.classList.remove('flex-1', 'flex', 'flex-col');
        }
      }
    };

    const unlockScroll = () => document.getElementsByTagName('body')[0].classList.remove('no-scroll');
    const lockScroll = () => document.getElementsByTagName('body')[0].classList.add('no-scroll');

    onMounted(() => {
      window.dataLayer.push({
        event: 'game_interactions',
        event_action: 'game_detail',
        game_name: game.name,
      });
    });

    return { isMobile, isPlay, playGame, src, game, breadcrumbRoutes, closeGame };
  },
});
</script>
